import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { Invitation } from "../../../Everou/_models/Invitation";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PMSLocationsActions } from "../../_actions/PMSLocationsActions";
import { TimeHelper } from "../../../Everou/_helpers/TimeHelper";
import { IsoRangeDatePicker } from "../../../Everou/_components/IsoRangeDatePicker";
import { Selector } from "../../../Common/_components/Selector";
import { ValidatedTextInput } from "../../../Common/_components/ValidatedTextInput";
import { SimpleToggle } from "../../../Everou/_components/SimpleToggle";
import { Icons } from "../../../Everou/_assets";


/**
 * The rooms an integration opens, resolved from the invitation groups it hangs
 * from: "Sala 1, Sala 2". Empty while the groups have not loaded, or when the
 * integration has none.
 */
function roomNamesOf(integration, locationGroups: any[] = []) {
    const names: string[] = [];
    for (const { id } of integration.groups ?? []) {
        const group = locationGroups.find(g => g.id === id);
        for (const room of group?.invitation?.rooms ?? []) {
            if (room.name && !names.includes(room.name)) names.push(room.name);
        }
    }
    return names.join(", ");
}

/**
 * One picker option per integration of the location, labelled so that two of
 * them never read the same: company, then whatever identifies the account
 * (the PMS property, the PMS room, the template's name) and the rooms it opens.
 */
function buildAvailablePMS(availableIntegrations: any[] = [], locationGroups: any[] = []) {
    return availableIntegrations.map((template) => {
        const hostName = template.host_name && template.host_name !== "None" ? template.host_name : "";
        // templateName last: a Manual integration has no property nor room
        // name of its own, so without it two of them read identically.
        const identity = hostName || template.room_name || template.templateName || "";
        // The rooms always travel: two integrations of one template (one
        // Manual account hung from two rooms) share every name above, and
        // only the rooms tell them apart.
        const rooms = roomNamesOf(template, locationGroups);
        const detail = identity && rooms
            ? `${identity} (${rooms})`
            : identity || rooms;
        const label = detail
            ? template.company + " - " + detail
            : template.company;
        return ({ value: template, label, isDisabled: !template.groups?.length });
    });
}

export function NewReservationInput({
    timezone,
    integrations
}) {
    let invitation = Invitation();
    invitation = useSelector(Selectors.getPMSReservationNewDraft);

    const dispatch = useDispatch();
    const { t } = useTranslation();

    // The location's invitation groups, with their rooms. An integration only
    // carries its group ids, so the rooms it opens have to be looked up here.
    const locationGroups = useSelector(Selectors.getPMSLocationGroups);

    const availablePMS = useMemo(
        () => buildAvailablePMS(integrations, locationGroups),
        [integrations, locationGroups],
    );
    const [selectedPMS, setSelectedPMS] = useState<any>(null);

    // GUEST (optional): collapsed by default, name, email and whether to
    // email the access link.
    const [guestSectionOpen, setGuestSectionOpen] = useState(false);
    const [guestName, setGuestName] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    const [sendEmail, setSendEmail] = useState(false);

    const onChangePMS = useCallback(option => {
        setSelectedPMS(option);
        dispatch(
            PMSLocationsActions.changeReservationGroup(
                option.value.uid,
                option.value.groups,
            ),
        );
    }, [dispatch]);

    const onChangeGuestName = useCallback(name => {
        setGuestName(name);
        dispatch(PMSLocationsActions.setReservationGuestName(name));
    }, [dispatch]);

    const onChangeGuestEmail = useCallback(email => {
        const hadEmail = !!guestEmail;
        setGuestEmail(email);
        dispatch(PMSLocationsActions.setReservationGuestEmail(email));
        // When an email is entered for the first time, default the "send
        // access link" choice to the integration's configured behaviour.
        if (!hadEmail && !!email) {
            const defaultSendEmail = !!selectedPMS?.value?.sendInvitationURL;
            setSendEmail(defaultSendEmail);
            dispatch(PMSLocationsActions.setReservationSendEmail(defaultSendEmail));
        }
    }, [dispatch, guestEmail, selectedPMS]);

    const onToggleSendEmail = useCallback(checked => {
        setSendEmail(checked);
        dispatch(PMSLocationsActions.setReservationSendEmail(checked));
    }, [dispatch]);

    // INITIALIZATION
    useEffect(() => {
        dispatch(PMSLocationsActions.changeReservationBeginTime(TimeHelper.getTodayIsoString(), true))
    }, [dispatch]);

    useEffect(() => {
        const selectableOptions = availablePMS.filter(option => !option.isDisabled);
        if (selectableOptions.length === 1)
            onChangePMS(selectableOptions[0]);
    }, [availablePMS, onChangePMS]);

    const selectablePMS = availablePMS.filter(option => !option.isDisabled);
    const hasPMSConfigured = selectablePMS.length > 0;
    const lockPMSSelection = selectablePMS.length <= 1;

    const content = (
        <div
            style={{
                gap: 24,
                gridTemplateColumns: "repeat(2, 400px)",
            }}
            className="m-auto mt-2 w-100 px-2"
        >
            {!hasPMSConfigured ? (
                <p className="text-left mb-0">{t("mod_pms_no_pms_configured")}</p>
            ) : (
                <>
                    <div className="c-min-w-40">
                        {lockPMSSelection ? (
                            <p className="text-left mb-0">{selectablePMS[0]?.label}</p>
                        ) : (
                            <Selector
                                hideSelectedOptions={false}
                                placeholder={t("mod_pms_select_pms_placeholder")}
                                value={selectedPMS}
                                onChange={onChangePMS}
                                options={availablePMS}
                            />
                        )}
                    </div>
                    <br />
                    <h3 className="text-left">{t("common.when")}</h3>
                    {dateRangeSelector(
                        timezone,
                        invitation,
                    )}
                    <br />
                    {guestSection()}
                </>
            )}
        </div>
    );

    function guestSection() {
        return (
            <div className="text-left">
                <h3
                    className="cp d-flex align-items-center justify-content-between m-0"
                    onClick={() => setGuestSectionOpen(open => !open)}
                >
                    {t("mod_pms_reservation_guest_section")}
                    <img
                        src={guestSectionOpen ? Icons.arrowUp : Icons.arrowDown}
                        alt=""
                        style={{ width: 16, height: 16 }}
                    />
                </h3>
                {guestSectionOpen && (
                    <div className="mt-2">
                        <label className="mb-1">{t("global_name")}</label>
                        <ValidatedTextInput
                            isEditable
                            value={guestName}
                            setValue={onChangeGuestName}
                            placeholder={t("global_name")}
                        />
                        <label className="mb-1 mt-2 d-block">{t("global_email")}</label>
                        <ValidatedTextInput
                            isEditable
                            value={guestEmail}
                            setValue={onChangeGuestEmail}
                            placeholder={t("global_email")}
                        />
                        {!!guestEmail && (
                            <div className="mt-3 d-flex align-items-center justify-content-between">
                                <span>{t("mod_pms_reservation_send_email")}</span>
                                <SimpleToggle
                                    checked={sendEmail}
                                    handleChange={e => onToggleSendEmail(e.detail.value)}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return content;

    function dateRangeSelector(timezone, invitation = Invitation()) {
        const { begin, end } = invitation;
        return (
            <div className="mt-1">
                <IsoRangeDatePicker
                    onChangeStart={handleStartDateChange}
                    onChangeEnd={handleEndDateChange}
                    timezone={timezone}
                    begin={begin}
                    end={end}
                    minDate={new Date()}
                />
            </div>
        );
    }

    function handleStartDateChange(localDateString) {
        if (localDateString === invitation.begin || localDateString === null)
            return;
        dispatch(
            PMSLocationsActions.changeReservationBeginTime(
                localDateString,
                invitation.newInvitation,
            ),
        );
    }

    function handleEndDateChange(localDateString) {
        console.log(localDateString);
        if (localDateString === invitation.end)
            return;
        dispatch(
            PMSLocationsActions.changeReservationEndTime(
                localDateString,
                invitation.newInvitation,
            ),
        );
    }
}